import React, { useCallback, useEffect, useState, useRef } from 'react';
import { DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { LoadingIndicator } from './LoadingIndicator';
import { LoadingState, LoadState } from './LoadingStateUtil';
import { ErrorIndicator } from './ErrorIndicator';
import { CancelButton } from './CancelButton';
import { PrimaryButton } from './PrimaryButton';
import { DialogOpenButton } from './DialogOpenButton';
import { useTranslation } from 'react-i18next';
import { GetAllSelectableFieldsForResearchProject, SelectableEntity } from '../data/SampleTrackingFieldSelectionData';
import { EntityFieldSelector } from './EntityFieldSelector';
import useLocalStorage from '../hooks/UseLocalStorage';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import useAuth from '../auth/UseAuth';
import { useParams } from 'react-router-dom';
import { filter, map } from 'lodash';
import { UseStateSetter } from '../util/TypeUtil';
import { PersistentDialog } from './PersistentDialog';
import { jsonIsEqual } from '../util/grid/TableUtils';

export function MemorizedTableLineageColumnSelectorDialog({
  defaultColumns,
  setAdditionalColumnEntities,
  setAllColumnEntities,
  entitiesFromProvidedColumns,
  cacheKey,
  selectedEntities,
}: {
  defaultColumns: ReadonlyArray<string>;
  setAdditionalColumnEntities: UseStateSetter<ReadonlyArray<SelectableEntity>>;
  setAllColumnEntities: UseStateSetter<ReadonlyArray<SelectableEntity>>;
  entitiesFromProvidedColumns: SelectableEntity[];
  cacheKey: string;
  selectedEntities?: ReadonlyArray<SelectableEntity>;
}) {
  return (
    <TableLineageColumnSelectorDialog
      onChange={entries => {
        setAdditionalColumnEntities(entries);
      }}
      onPopulate={setAllColumnEntities}
      defaultColumns={defaultColumns}
      additionalEntities={entitiesFromProvidedColumns}
      cacheKey={cacheKey}
      selectedEntities={selectedEntities}
    />
  );
}

export const MemoizedColumnSelectorDialog = React.memo(
  MemorizedTableLineageColumnSelectorDialog,
  (prevProps, nextProps) => {
    return (
      jsonIsEqual(prevProps.defaultColumns, nextProps.defaultColumns) &&
      prevProps.setAdditionalColumnEntities === nextProps.setAdditionalColumnEntities &&
      prevProps.setAllColumnEntities === nextProps.setAllColumnEntities &&
      prevProps.cacheKey === nextProps.cacheKey &&
      jsonIsEqual(prevProps.entitiesFromProvidedColumns, nextProps.entitiesFromProvidedColumns) &&
      jsonIsEqual(prevProps.selectedEntities, nextProps.selectedEntities)
    );
  }
);

export const TableLineageColumnSelectorDialog = ({
  onChange,
  onPopulate,
  defaultColumns,
  additionalEntities,
  cacheKey,
  selectedEntities,
}: {
  onChange: (entities: ReadonlyArray<SelectableEntity>) => void;
  onPopulate: UseStateSetter<ReadonlyArray<SelectableEntity>>;
  defaultColumns: ReadonlyArray<string>;
  additionalEntities: SelectableEntity[];
  cacheKey: string;
  selectedEntities?: ReadonlyArray<SelectableEntity>;
}) => {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  let { researchProjectId } = useParams() as { researchProjectId: string };

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<LoadingState>({ status: 'Complete' });

  const [entities, setEntities] = useState<ReadonlyArray<SelectableEntity>>([]);
  const [defaultEntities, setDefaultEntities] = useState<SelectableEntity[]>([]);
  const [localStorageEntities, setLocalStorageEntities, localStorageEntitiesKeyPresent] = useLocalStorage<
    ReadonlyArray<SelectableEntity>
  >(`${cacheKey}-column-selection`, []);
  const [initialized, setInitialized] = useState<boolean>(false);
  const fetchedForProjectId = useRef<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedOnChange = useCallback(onChange, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memorizedOnPopulate = useCallback(onPopulate, []);

  useEffect(() => {
    if (initialized || fetchedForProjectId.current === researchProjectId) {
      return;
    }

    if (localStorageEntities.length > 0) {
      memoizedOnChange(localStorageEntities);
      setInitialized(true);
      return;
    }
  }, [memoizedOnChange, localStorageEntities, initialized, researchProjectId]);

  useEffect(() => {
    if (accessToken === undefined || defaultEntities.length > 0 || fetchedForProjectId.current === researchProjectId) {
      return;
    }

    fetchedForProjectId.current = researchProjectId;

    return LoadState(setLoadingState, async () => {
      const entitiesData = [
        ...additionalEntities,
        ...(await GetAllSelectableFieldsForResearchProject(researchProjectId, accessToken)),
      ];

      const newDefaults = filter(
        map(entitiesData, entity => {
          return {
            ...entity,
            fields: filter(entity.fields, field => {
              return defaultColumns.includes(field.key);
            }),
          };
        }),
        entity => entity.fields.length > 0
      );

      memorizedOnPopulate(prevState => {
        // Data won't change, avoid re-renders by returning current state
        if (prevState.length > 0) {
          return prevState;
        }

        return entitiesData;
      });
      setDefaultEntities(newDefaults);

      if (localStorageEntitiesKeyPresent) {
        return;
      }

      setLocalStorageEntities(newDefaults);

      if (!initialized) {
        memoizedOnChange(newDefaults);
        setInitialized(true);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessToken,
    additionalEntities,
    defaultColumns,
    defaultEntities,
    localStorageEntities,
    localStorageEntitiesKeyPresent,
    memorizedOnPopulate,
    memoizedOnChange,
    researchProjectId,
    setLocalStorageEntities,
  ]);

  function handleClose() {
    setModalOpen(false);
    setLoadingState({ status: 'Complete' });
  }

  function handleConfirm() {
    setLocalStorageEntities(entities);
    onChange(entities);
    handleClose();
  }

  return (
    <>
      <DialogOpenButton
        testId='column-selector-button'
        onClick={() => setModalOpen(true)}
        sx={{ padding: 1, minWidth: 3 }}
      >
        <ViewWeekOutlinedIcon />
      </DialogOpenButton>
      <PersistentDialog open={modalOpen} onClose={handleClose} maxWidth={'xs'} fullWidth>
        <DialogTitle>
          {t('columns')}
          <Typography variant='subtitle1'>{t('columnSelectorLineageInfo')}</Typography>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: 0 }}>
          <Typography sx={{ whiteSpace: 'pre-line' }}></Typography>
          <EntityFieldSelector
            onChange={entries => {
              setEntities(entries);
            }}
            defaultEntities={defaultEntities}
            additionalEntities={additionalEntities}
            selectedEntities={selectedEntities}
          />
        </DialogContent>
        <LoadingIndicator loadingState={loadingState} margin={'LR'} />
        <ErrorIndicator loadingState={loadingState} />
        <DialogActions>
          <CancelButton onClick={handleClose} />
          <PrimaryButton disabled={loadingState.status === 'Loading'} onClick={handleConfirm}>
            {t('confirm')}
          </PrimaryButton>
        </DialogActions>
      </PersistentDialog>
    </>
  );
};
